import { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import { LOCAL_STORAGE_USERHASH } from 'constants.js';
import { getUserLogin } from 'containers/User/service';
import { getLogin } from 'action-creators/loginRedirect';
import { logoutService } from 'api/services/user/logout';
import { getClaroAdsKey } from 'utils/claroAds/getClaroAdskey';
import { set } from 'utils/LocalStorageUtil';
import { UserContext } from '../../../providers/user.provider';

const USERHASH_PARAM_KEYS = ['user_hash', 'userhash'];

const readUserhash = params =>
  params.get('user_hash') || params.get('userhash');

const stripUserhash = params => {
  const next = new URLSearchParams(params);
  let changed = false;
  USERHASH_PARAM_KEYS.forEach(key => {
    if (next.has(key)) {
      next.delete(key);
      changed = true;
    }
  });
  return { next, changed };
};

export const useMundialUserhashLogin = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const userContext = useContext(UserContext);
  const claroLiteConfig = useSelector(state => getClaroAdsKey(state));
  const startedRef = useRef(false);
  const [status, setStatus] = useState(() => {
    const newUserHash = readUserhash(searchParams);
    if (!newUserHash) return 'idle';
    const oldUserHash = localStorage.getItem(LOCAL_STORAGE_USERHASH);
    if (oldUserHash && oldUserHash === newUserHash) return 'idle';
    return 'pending';
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (startedRef.current) return;

    const newUserHash = readUserhash(searchParams);
    if (!newUserHash) return;

    const oldUserHash = localStorage.getItem(LOCAL_STORAGE_USERHASH);

    const cleanUrl = () => {
      const { next, changed } = stripUserhash(searchParams);
      if (changed) setSearchParams(next, { replace: true });
    };

    if (oldUserHash && oldUserHash === newUserHash) {
      cleanUrl();
      return;
    }

    startedRef.current = true;

    const run = async () => {
      try {
        set('selectProfile', { enable: true });

        if (oldUserHash && oldUserHash !== newUserHash) {
          await logoutService({ claroLiteConfig });
        }

        const request = await getUserLogin({ userhash: newUserHash });
        localStorage.removeItem('logOutForce');
        const response = get(request, 'response');

        const loginApp = userContext?.login;
        if (response?.lasttouch && typeof loginApp === 'function') {
          await loginApp(response, () => {});
        } else {
          dispatch(getLogin(response));
        }

        cleanUrl();
        setStatus('success');
      } catch (err) {
        console.error(err);
        setError(err);
        setStatus('error');
      }
    };

    run();
  }, []);

  return { status, error };
};

export default useMundialUserhashLogin;
